import { addToast } from "@caprikorn/dynamo-ui";
import { useMutation, useQueryClient } from "@tanstack/react-query";
// eslint-disable-next-line caprikorn-rules/use-custom-translations
import { useTranslation } from "react-i18next";

import { useGroupReferences } from "@contexts/GroupReferences/GroupReferences.context";
import { apiEcmsHttp } from "@services/http";
import { QUERY_KEYS } from "@services/keys";
import { useCustomTranslation } from "@utils/translations/useCustomTranslation";

import type { TechnicalRequestData } from "./apiEcms.types";

const SECTION_ORDER: Record<string, number> = {
  perimeter: 1,
  contact: 2,
  potencyForm: 3,
  cancellationForm: 3,
  onSiteContact: 4,
  comment: 5,
};

const FALLBACK_SECTION_ORDER = Object.keys(SECTION_ORDER).length + 1;

export const usePostTechnicalRequest = () => {
  const { currentGroup } = useGroupReferences();
  // useTranslation is required here to get the raw translation sent to the backend
  const { t: i18nT } = useTranslation(["main", "common"]);
  const { t } = useCustomTranslation(["main", "common"]);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (form: TechnicalRequestData) => {
      const formData = new FormData();

      formData.append("identifier", form.identifier);

      const sections = Object.entries(form.description)
        .map(([sectionKey, fields]) => {
          const items = Object.entries(fields)
            .map(
              ([fieldKey, value]) =>
                `<li><strong>${i18nT("technicalRequestField", { ns: "common", context: fieldKey })} :</strong> ${value}</li>`,
            )
            .join("");
          return {
            order: SECTION_ORDER[sectionKey] ?? FALLBACK_SECTION_ORDER,
            html: `<h1>${i18nT("technicalRequestField", { ns: "common", context: sectionKey })}</h1><ul>${items}</ul>`,
          };
        })
        .sort((a, b) => a.order - b.order)
        .map((section) => section.html)
        .join("");
      formData.append("description", `<div>${sections}</div>`);

      formData.append(
        "actionType",
        i18nT(`requestDrawer.steps.actionType.options.${form.actionType}`),
      );
      if (form.dueDate) {
        formData.append("dueDate", form.dueDate);
      }
      if (form.siteId) {
        formData.append("siteId", form.siteId);
      }
      form.attachments?.forEach((attachment) =>
        formData.append("attachments[]", attachment),
      );

      return apiEcmsHttp.post("/technical-request", formData);
    },
    retry: false,
    mutationKey: [QUERY_KEYS.TECHNICAL_REQUEST_CREATION, currentGroup],
    onSuccess: () => {
      addToast({
        variant: "success",
        title: t("requestDrawer.successTitle"),
        description: t("requestDrawer.successDescription"),
      });
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.TECHNICAL_REQUESTS],
        refetchType: "active",
      });
    },
    onError: () => {
      addToast({
        variant: "error",
        title: t("requestDrawer.errorTitle"),
        description: t("requestDrawer.errorDescription"),
      });
    },
  });
};
